import { reportError } from '@/utils/tracking';
import { Box, useTheme } from '@mui/material';
import { useAtom } from 'jotai';
import moment from 'moment-timezone';
import { ErrorInfo, Component as ReactComponent, ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import logo from '../assets/logo.svg';
import { useClearAuthenticationState } from '../auth/authentication';
import { If } from '../components/utils/conditionalComponents';
import { PreferNoWrap } from '../components/utils/textWrapping';
import { TECH_SUPPORT_EMAIL } from '../constants/deprecated';
import { momentAtomWithSessionStorage } from '../utils/browserStorage';
import { Children, Component, ReturnComponent } from '../utils/reactTypes';

const MILLISECONDS_BEFORE_ERROR_FALLBACK_DISPLAY = 100;
const MIN_MINUTES_BEFORE_ANOTHER_HARD_REFETCH = 10;

// Timestamp to track when we tried to last reload the page in hopes of fixing a top-level error
const lastHardRefetchMomentAtom = momentAtomWithSessionStorage(
    'lastHardRefetchMoment',
    moment().subtract(MIN_MINUTES_BEFORE_ANOTHER_HARD_REFETCH, 'minutes'),
);

export const useTryRecoveringFromError = ({
    clearAuth,
    reload,
}: {
    clearAuth: boolean;
    reload: boolean;
}) => {
    const clearAuthenticationState = useClearAuthenticationState();
    const [lastHardRefetch, setLastHardRefetch] = useAtom(lastHardRefetchMomentAtom);

    const tryRecoveringFromError = (error?: Error) => {
        // If we get an error that triggers the top-level error boundary, we clear all local state.
        //  This helps with avoiding them getting bricked out due to authentication issues, stale data, etc.
        //  For page-level/component-level error boundaries, do not clear auth; that would be annoying.
        if (clearAuth) clearAuthenticationState();

        // Sometimes, an error is solved by reloading the page, so if the top-level error boundary
        //  is triggered, we try doing just that.
        // However, if we encounter another top-level error again within, say, the next 10 minutes,
        //  we don't reload, as it would seem reloading didn't fix the error, and it really is an
        //  error that needs to be fixed.
        if (reload) {
            // Dynamic import failures (e.g. Vite module hash change after branch switch) are always
            // safe to reload on — bypass the throttle so the page recovers automatically.
            const isModuleFetchError =
                error instanceof TypeError &&
                error.message.includes('Failed to fetch dynamically imported module');

            if (
                !isModuleFetchError &&
                moment().diff(lastHardRefetch, 'minutes') < MIN_MINUTES_BEFORE_ANOTHER_HARD_REFETCH
            ) {
                // If it's been less than, say, 10 minutes since the last time we tried reloading
                //  the page, then we don't do it again.
            } else {
                // Otherwise, record this time and try reloading.
                setLastHardRefetch(moment());
                window.location.reload();
            }
        }
    };

    return tryRecoveringFromError;
};

export const ErrorFallback = (): ReturnComponent => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);

    // This is so that if we are in the midst of hard re-fetching, they will not see the error fallback message
    useEffect(() => {
        setTimeout(() => setLoading(false), MILLISECONDS_BEFORE_ERROR_FALLBACK_DISPLAY);
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: theme.spacing(6),
                textAlign: 'center',
            }}
        >
            <If condition={!loading}>
                <img
                    src={logo}
                    style={{
                        height: 200,
                    }}
                    alt='Axle Health logo'
                />
                <Box
                    sx={{
                        marginTop: theme.spacing(4),
                        maxWidth: '800px',
                        fontSize: '35px',
                        lineHeight: 1.25,
                    }}
                >
                    <p>
                        <PreferNoWrap>Unexpected error;</PreferNoWrap>{' '}
                        <PreferNoWrap>try refreshing the page.</PreferNoWrap>
                    </p>
                    <p>
                        If the problem persists, then please contact us at{' '}
                        <a href={`mailto:${TECH_SUPPORT_EMAIL}`}>{TECH_SUPPORT_EMAIL}</a>.
                    </p>
                </Box>
            </If>
        </Box>
    );
};

type GlobalErrorBoundaryProps = {
    children: ReactNode;
};

type GlobalErrorBoundaryState = {
    hasError: boolean;
};

// GlobalErrorBoundary: A temp solution to catch & report ONLY Apollo provider errors without modifying AxleErrorBoundary
export class GlobalErrorBoundary extends ReactComponent<
    GlobalErrorBoundaryProps,
    GlobalErrorBoundaryState
> {
    public state: GlobalErrorBoundaryState = {
        hasError: false,
    };

    public static getDerivedStateFromError(_: Error): GlobalErrorBoundaryState {
        return {
            hasError: true,
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        reportError(error, { ...errorInfo, isGlobal: true });
    }

    public render() {
        if (this.state.hasError) {
            return <ErrorFallback />;
        }

        return this.props.children;
    }
}

// AxleErrorBoundary: Error Boundary
// todo: refactor to improve reporting and prevent user logout
export const AxleErrorBoundary = ({
    children,
    fallback = <ErrorFallback />,
    clearAuth = false,
    reload = false,
}: {
    children: Children;
    fallback?: Component;
    clearAuth?: boolean;
    reload?: boolean;
}): ReturnComponent => {
    const tryRecoveringFromError = useTryRecoveringFromError({ clearAuth, reload });

    return (
        <ErrorBoundary fallback={fallback} onError={(error) => tryRecoveringFromError(error)}>
            {children}
        </ErrorBoundary>
    );
};
